import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/feedback/Skeleton'
import { Card } from '@/components/ui/Card'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { useQuoteStatistics } from '@/features/quotes/api/useQuote'
import { formatNumber, formatPercent } from '@/lib/format'

/* The pipeline's win/loss figures (FR-15, Story 2.7 AC 5 and 6).
 *
 * WHOSE NUMBERS THESE ARE IS PART OF THE NUMBER. A Sales user's statistics are
 * counted over their own quotes, and a personal conversion rate read as a
 * company conversion rate is the failure AC 6 exists to prevent — so the caveat
 * is a permanent line in the widget, beside the title, never a tooltip and
 * never behind an info icon. A caveat you have to hover for is a caveat the
 * person who most needs it will not see. The label comes from the SERVER
 * (`scopeLabel`), because the client would otherwise have to work out which
 * role is asking and would promise "your quotes only" over a set somebody's
 * scope was widened to include.
 *
 * EXPIRED IS ITS OWN FIGURE, beside Won and Lost, never folded into Lost. A
 * quote nobody answered is a different failure from one the client refused: the
 * first is a chase that was not made, the second is a price that was not right,
 * and a surface that added them would hide which of the two this month was made
 * of.
 *
 * ONE PANEL, NOT FIVE TILES. The five figures are a single reading — won, lost,
 * expired, the rate between them, and what the wins were worth — and a row of
 * separate cards invites the eye to take them one at a time. So they share a
 * card and are divided by hairlines, the same separation the dense table uses
 * for columns belonging to one row. The dashboard's KpiTile is deliberately NOT
 * reused here for that reason: a KpiTile IS a card, and five cards is the shape
 * this panel is not. What the two surfaces still share is the type — the same
 * 26px semibold figure over the same muted label — so the founder's dashboard
 * and the salesperson's pipeline go on reading as one product.
 *
 * The caveat and the served period ride the panel's own header line, beside the
 * title and separated by bullets, so the sentence that qualifies every figure
 * below is read on the way in rather than stacked above the card.
 */

/** The periods the widget offers. `all` is unbounded — see rangeFor. */
type Period = 'd30' | 'd90' | 'year' | 'all'

const PERIOD_OPTIONS = [
  { value: 'd30', labelKey: 'quotes.statistics.range.d30' },
  { value: 'd90', labelKey: 'quotes.statistics.range.d90' },
  { value: 'year', labelKey: 'quotes.statistics.range.year' },
  { value: 'all', labelKey: 'quotes.statistics.range.all' },
] as const satisfies readonly { value: Period; labelKey: string }[]

const DAY_MS = 86_400_000

/** A date-only wire value (AD-19) from an instant, read in UTC.
 *
 *  UTC rather than the browser's zone, matching parseDateOnly's half of the
 *  same contract in lib/format: a range boundary built from local getDate()
 *  west of Greenwich asks the server for yesterday, and the widget would show a
 *  period one day short of the one printed on it. */
function dateOnly(instant: Date): string {
  return instant.toISOString().slice(0, 10)
}

/** The range a period asks the server for, from ONE instant.
 *
 *  The instant is a parameter rather than a `new Date()` inside each branch,
 *  and that is the point: two clock reads a millisecond either side of midnight
 *  produce a `from` and a `to` from different days, and the widget would then
 *  label a 31-day count as thirty. The same reasoning the server applies to
 *  expiry, where the day is passed into the count rather than read per row.
 *
 *  `all` sends NOTHING — not an epoch, not the first quote's date. An unbounded
 *  end is absent on the wire in both directions (PipelineStatisticsDTO omits
 *  it), and inventing a first day would put a date on the screen that means
 *  nothing to the reader. */
function rangeFor(period: Period, now: Date): { from?: string; to?: string } {
  if (period === 'all') return {}

  const to = dateOnly(now)
  if (period === 'year') {
    return { from: `${now.getUTCFullYear()}-01-01`, to }
  }

  /* days − 1, and the subtraction is the whole point: the server counts BOTH
     ends of the range, so a `from` a full thirty days back and a `to` of today
     is thirty-one calendar days under a control that says thirty. Today is the
     first of the thirty, so only twenty-nine come off. */
  const days = period === 'd30' ? 30 : 90
  return { from: dateOnly(new Date(now.getTime() - (days - 1) * DAY_MS)), to }
}

/** One figure, its label and the sentence that says what it counts.
 *
 *  A `<section>` per cell rather than a `<div>`: each figure is a self-contained
 *  reading under its own heading, and the region is what lets a screen reader —
 *  and a test — take "Expired: 4" without dragging the four beside it along.
 *
 *  The hint is not decoration. "Won" alone does not say that Converted counts as
 *  won, and a reader who guesses wrong misreads every figure in the row. */
function StatCell({
  label,
  value,
  unit,
  hint,
}: {
  label: string
  value: string
  unit?: string
  hint?: string
}) {
  return (
    <section
      className={cn(
        'border-neutral-100 flex flex-col gap-1.5 px-[18px] py-4',
        // Two up on a phone: a rule between the pair, and one above every row
        // after the first, so the block reads as a grid rather than as four
        // figures that happen to have landed near each other.
        'even:border-l [&:nth-child(n+3)]:border-t',
        // Five up from `lg`: a single row, one rule before every cell but the
        // first. The card's own border closes the ends.
        'lg:border-t-0 lg:border-l lg:first:border-l-0',
      )}
    >
      <h3 className="text-text-muted text-[13px] leading-tight font-normal">{label}</h3>
      <p className="asa-num text-text-heading text-[26px] leading-none font-semibold tracking-[-0.02em] whitespace-nowrap">
        {value}
        {unit ? (
          <span className="text-text-muted ml-1 text-[13px] font-normal">{unit}</span>
        ) : null}
      </p>
      {/* Reserved even when absent, so a cell without a hint keeps the height of
          the four beside it and the figures stay on one baseline. */}
      <p className="text-text-muted min-h-[16px] text-[12px] leading-snug font-light">
        {hint}
      </p>
    </section>
  )
}

/** The separator between two facts on the header line. Presentational — a
 *  screen reader meets the parts as separate elements and needs no bullet. */
function Bullet() {
  return (
    <span className="text-text-faint text-[12px] leading-none" aria-hidden>
      •
    </span>
  )
}

export function PipelineStatistics() {
  const { t } = useTranslation()
  /* The LOCALE-BOUND date, not lib/format's default: this line sits beside a
     table whose every date is written «31 авг. 2026 г.», and an English month
     name in the middle of a Russian screen reads as a fault in the page. */
  const format = useFormat()
  const [period, setPeriod] = useState<Period>('d30')

  /* Recomputed when the PERIOD changes, not on every render: the memo is what
     pins the instant, so the same "last 30 days" keeps asking for the same 30
     days for as long as the user leaves the control alone. Re-reading the clock
     each render would mint a new range at midnight and refetch under a reader
     who had not touched anything.

     The cost of pinning it is that a tab left open for days keeps asking for a
     window anchored where it was opened — a real shape on the shared
     workstations this product is used from. That is why the SERVED period is
     printed on the header line rather than the control's label alone: the panel
     states the days it actually counted, so a stale anchor is visible as a date
     rather than hidden behind the words "Last 30 days". */
  const range = useMemo(() => rangeFor(period, new Date()), [period])

  const { data, isPending, isError } = useQuoteStatistics(range)

  return (
    <Card aria-label={t('quotes.statistics.title')}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-[18px] pt-4 pb-3.5">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-text-heading text-[14px] leading-none font-medium">
            {t('quotes.statistics.title')}
          </h2>
          {/* AC 6. Rendered as soon as there are figures to qualify and never
              hidden behind an interaction — the sentence and the numbers are
              read together or the numbers are read wrong. */}
          {data ? (
            <>
              <Bullet />
              <p className="text-text-muted text-[12.5px] leading-none font-light">
                {t(`quotes.statistics.scope.${data.scopeLabel}`)}
              </p>
            </>
          ) : null}
          {/* The days the server actually counted, which is why the DTO carries
              them at all. The period control says what was ASKED for; this says
              what came back, and the two part company whenever the anchor above
              has been sitting still — an open tab, a workstation nobody logged
              out of. Absent on "all time", where there is no first day to
              print and inventing one would be a date that means nothing. */}
          {data?.from && data.to ? (
            <>
              <Bullet />
              <p className="text-text-muted text-[12.5px] leading-none font-light">
                {t('quotes.statistics.period', {
                  from: format.date(data.from),
                  to: format.date(data.to),
                })}
              </p>
            </>
          ) : null}
        </div>

        <SegmentedControl
          label={t('quotes.statistics.range.label')}
          options={PERIOD_OPTIONS}
          value={period}
          onChange={setPeriod}
        />
      </div>

      {isPending ? (
        <div className="border-neutral-100 border-t p-[18px]">
          <Skeleton className="h-[78px] rounded-[var(--radius-DEFAULT)]" />
        </div>
      ) : isError ? (
        /* Quiet and inline, and the pipeline below it stays exactly where it
           was. The statistics are a secondary reading of a list that is still
           perfectly legible without them, so an ErrorState that replaced the
           surface would take a working screen down for a figure. */
        <p className="border-neutral-100 text-text-muted border-t px-[18px] py-4 text-[13px] leading-snug font-light">
          {t('quotes.statistics.error')}
        </p>
      ) : (
        <div className="border-neutral-100 grid grid-cols-2 border-t lg:grid-cols-5">
          <StatCell
            label={t('quotes.statistics.won')}
            value={formatNumber(data.won, 0)}
            hint={t('quotes.statistics.wonHint')}
          />
          <StatCell
            label={t('quotes.statistics.lost')}
            value={formatNumber(data.lost, 0)}
            hint={t('quotes.statistics.lostHint')}
          />
          <StatCell
            label={t('quotes.statistics.expired')}
            value={formatNumber(data.expired, 0)}
            hint={t('quotes.statistics.expiredHint')}
          />
          {/* formatPercent answers the em dash for null, which is what a null
              conversion rate must render: nothing in the range was decided, and
              a manufactured 0% would report that the team lost every quote in a
              month they were still waiting on.

              The hint names the DENOMINATOR, and counts it the way the server
              counts it — `Outcomes.Decided` in backend/internal/quote/
              statistics.go is won + lost + expired, because a quote that ran
              out of validity is a chance the pipeline had and did not take.
              Adding three served integers is not recomputing the rate: the
              percentage itself still arrives from the server and is never
              derived here, so the figure and its denominator cannot disagree
              about arithmetic neither of them performs twice. */}
          <StatCell
            label={t('quotes.statistics.conversion')}
            value={formatPercent(data.conversionRate)}
            hint={t('quotes.statistics.conversionHint', {
              won: formatNumber(data.won, 0),
              decided: formatNumber(data.won + data.lost + data.expired, 0),
            })}
          />
          {/* Money as it arrives — a decimal string, formatted, never parsed
              and re-totalled here (AD-9). The currency trails as the cell's
              unit, the way every other figure in the product is written. */}
          <StatCell
            label={t('quotes.statistics.wonValue')}
            value={formatNumber(data.wonSellTotal.amount, 0)}
            unit={data.wonSellTotal.currency}
          />
        </div>
      )}
    </Card>
  )
}
